import type { NonSlotFlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import type { ApplicableVirtualMediaDto } from '@/types/dtos/media/virtual-media.dto';

const DEFAULT_SORT_ORDER = 9999;

export function getVirtualMediaScopeSortKey(
  scope: NonSlotFlowPlaylistScope | 'slot',
  slotContentType?: string,
): string {
  if (scope === 'slot' && slotContentType) {
    return `slot:${slotContentType}`;
  }
  return scope;
}

export function resolveCompositeSortOrder(
  sortOrderByScope: Record<string, number> | undefined,
  sortKey: string,
  scope: string,
): number {
  return sortOrderByScope?.[sortKey] ?? sortOrderByScope?.[scope] ?? DEFAULT_SORT_ORDER;
}

type CompositeSortInput = {
  name: string;
  sortOrderByScope?: Record<string, number>;
};

export function compareCompositesByScopeSort(
  a: CompositeSortInput,
  b: CompositeSortInput,
  sortKey: string,
  scope: string,
): number {
  const orderA = resolveCompositeSortOrder(a.sortOrderByScope, sortKey, scope);
  const orderB = resolveCompositeSortOrder(b.sortOrderByScope, sortKey, scope);
  if (orderA !== orderB) {
    return orderA - orderB;
  }
  return a.name.localeCompare(b.name);
}

export function resolveApplicableVirtualMediaSortOrder(
  dtoSortOrder: number,
  sortOrderByScope: Record<string, number> | undefined,
  sortKey: string,
  scope: string,
): number {
  if (sortOrderByScope) {
    return resolveCompositeSortOrder(sortOrderByScope, sortKey, scope);
  }
  return dtoSortOrder;
}

export function compareApplicableVirtualMediaByScope(
  a: ApplicableVirtualMediaDto,
  b: ApplicableVirtualMediaDto,
  scope: string,
  slotContentType: string | undefined,
  compositesBySlug: Map<string, CompositeSortInput>,
): number {
  const sortKey = getVirtualMediaScopeSortKey(scope as NonSlotFlowPlaylistScope | 'slot', slotContentType);
  const orderA = resolveApplicableVirtualMediaSortOrder(
    a.sortOrder,
    compositesBySlug.get(a.slug)?.sortOrderByScope,
    sortKey,
    scope,
  );
  const orderB = resolveApplicableVirtualMediaSortOrder(
    b.sortOrder,
    compositesBySlug.get(b.slug)?.sortOrderByScope,
    sortKey,
    scope,
  );
  if (orderA !== orderB) {
    return orderA - orderB;
  }
  return a.name.localeCompare(b.name);
}
